// AutoresGrid.js — Página /autores (busca/listagem de autores).
// Espelha o layout de GridProduto (/livros), porém voltado a autores:
//   - Categorias num popup, no cabeçalho (filtram autores pela categoria dos
//     seus livros, de forma defensiva — ver autorCategorias()). Era uma coluna
//     fixa à esquerda até 11/09/2026; ver BotaoCategorias.
//   - Título da categoria + contagem + busca + ordenação, numa faixa que
//     acompanha o scroll no desktop (ver GRUDA_NO_TOPO).
//   - Grade de autores no cartão "folha de caderno" (o mesmo da home).
//   - Estado vazio com a ilustração compartilhada (ui/CaixaVazia).
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Pagination from "../utils/Pagination";
import { BotaoCategorias, DrawerFiltros } from "../produto/gridProduto/PainelFiltros";
import RevealStagger from "../ui/RevealStagger";
import CaixaVazia from "../ui/CaixaVazia";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../hooks/useBarraGrudada";
import MarcaPadrao from "../ui/MarcaPadrao";

// A grade é de 5 colunas no desktop e 3 no tablet; 15 é o menor número que
// fecha as linhas dos dois (3×5 e 5×3), então nenhuma página termina com uma
// fileira pela metade. Eram 10, herdados da lista vertical, onde qualquer
// número fechava. A duas colunas (celular) sobra um cartão — é o resto normal
// de grade estreita, e fechar os três breakpoints exigiria 30 por página.
const AUTORES_PER_PAGE = 15;

// Classes do bloco que gruda no desktop: a faixa de cabeçalho do conteúdo.
// Eram dois, com a coluna de categorias ao lado, até ela virar popup em
// 11/09/2026 — a constante ficou porque a conta é longa demais para ser
// repetida de cabeça.
//
// A transição de `top` copia a do cabeçalho do site (App.css: 380ms,
// cubic-bezier(0.4, 0, 0.2, 1), que é o `ease-in-out` do Tailwind). Sem ela o
// bloco daria um salto de 184px enquanto o cabeçalho ainda estava deslizando.
//
// Está replicado em GridProduto.js, e não importado, porque são só duas telas
// com este layout de duas colunas. Se uma terceira precisar do mesmo, aí sim
// vale promover a constante (e o cálculo de --topo-fixo) a um módulo comum —
// duplicar uma terceira vez é como as duas páginas passam a grudar em alturas
// diferentes sem ninguém notar.
//
// E ELA SOME QUANDO O CABEÇALHO DO SITE VOLTA (pedido do dono, 11/09/2026).
// Rolando para cima, o cabeçalho reaparece e as duas faixas se empilhavam no
// topo, comendo um terço da tela justamente no gesto de voltar. A regra é a
// mesma da barra do perfil e da estante: com o cabeçalho à vista, a faixa
// grudada sai de cena; ela só assume o topo quando ele sai (useBarraGrudada).
// `CLASSES_SUMICO` já traz a transição de `top` junto com a de transform e
// opacity — duas classes `transition-[...]` no mesmo elemento brigariam, e
// quem ganhasse na ordem do CSS apagaria a outra.
const GRUDA_NO_TOPO =
  "md:sticky md:top-[var(--topo-fixo)] md:self-start md:z-30 " +
  CLASSES_SUMICO;

// Ordenações disponíveis para autores
const ORDENS = [
  // "Mais Popular" = mais VENDIDO. Era "Mais Relevante", que não dizia por qual
  // régua — e a régua era o número de publicações, a mesma da opção de baixo.
  { key: "popular", label: "Mais Popular" },
  { key: "publicacoes", label: "Mais Publicações" },
  { key: "az", label: "Nome (A-Z)" },
  { key: "za", label: "Nome (Z-A)" },
];


// Resolve a URL da foto do autor (full URL ou arquivo público da API)
const resolveFoto = (a, API_URL) => {
  const f = a.foto || a.imagem || a.avatar || a.profile_image || a.image || null;
  if (!f) return null;
  return /^https?:\/\//i.test(f) ? f : `${API_URL}public/${f}`;
};

// ================= O MESMO PAINEL DE /livros (11/09/2026) =================
//
// A coluna de Categorias daqui era um `<select>` disfarçado: um botão que abria
// uma lista de nomes, sem contagem e sem busca. A de /livros é uma faceta —
// cada linha diz quantos resultados ela traz, e uma barrinha filtra a própria
// lista de categorias. O dono pediu para as duas telas terem a MESMA, e agora
// elas têm literalmente o mesmo componente: `BotaoCategorias` (o popup do
// cabeçalho) e `DrawerFiltros` (o bottom-sheet do celular), com o estado
// vivendo aqui. A coluna fixa de 260px que as duas telas herdaram saiu em
// 11/09/2026 — ver BotaoCategorias.
//
// A CONTAGEM É DE AUTORES, não de livros: é o que esta página lista. "Direito
// 14" aqui significa catorze autores com livro de Direito — o mesmo rótulo em
// /livros significa catorze livros, e cada tela conta o que mostra.
// Normaliza nome de categoria (string ou objeto { nome })
const nomeCategoria = (c) => (typeof c === "string" ? c : c?.nome);

function AutoresGrid() {
  const main = process.env.REACT_APP_MAIN;
  // A faixa do título some quando o cabeçalho do site volta (ver
  // GRUDA_NO_TOPO); `margemNegativa: 16` é o `md:-mt-4` dela.
  const {
    barraRef: faixaRef,
    sentinelaRef: faixaSentinelaRef,
    esconder: esconderFaixa,
  } = useBarraGrudada({ margemNegativa: 16 });
  const API_URL = process.env.REACT_APP_API;
  const location = useLocation();
  const navigate = useNavigate();

  const [autores, setAutores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Filtros (estado a partir da URL) ----
  const params = new URLSearchParams(location.search);
  const buscaURL = params.get("busca") || "";
  const categoriaURL = params.get("categoria") || "";
  const ordemURL = params.get("ordem") || "popular";

  const [categoriaSel, setCategoriaSel] = useState(categoriaURL);
  // "relevante" era o padrão e virou "popular" em 11/09/2026. A chave antiga
  // continua aceita porque ela está em links já compartilhados.
  const [ordem, setOrdem] = useState(
    ORDENS.some((o) => o.key === ordemURL) ? ordemURL : "popular"
  );
  const [dropdownOrdem, setDropdownOrdem] = useState(false);
  // A barrinha que filtra a LISTA DE CATEGORIAS — não os autores. Mora só no
  // painel, não vai para a URL e some ao fechar o bottom-sheet.
  const [buscaCategoria, setBuscaCategoria] = useState("");
  // A busca DESTA página. O valor que filtra é o da URL (`busca`), o mesmo
  // parâmetro que a barra do cabeçalho usa — as duas escrevem no mesmo lugar,
  // então nunca discordam. O estado local existe só para o cursor não pular
  // enquanto se digita.
  const [textoBusca, setTextoBusca] = useState(buscaURL);
  const [painelAberto, setPainelAberto] = useState(false); // bottom-sheet do celular
  const [pagina, setPagina] = useState(1);

  // Sincroniza categoria/ordem quando a URL muda (clique vindo do header)
  useEffect(() => {
    setCategoriaSel(categoriaURL);
  }, [categoriaURL]);

  // Busca autores + categorias
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [rAutores, rCat, rEbook] = await Promise.all([
          fetch(API_URL + "autores"),
          fetch(API_URL + "categorias"),
          fetch(API_URL + "ebook"),
        ]);
        if (rAutores.ok) {
          const data = await rAutores.json();
          if (Array.isArray(data)) setAutores(data);
        }
        if (rCat.ok) {
          const data = await rCat.json();
          if (Array.isArray(data)) setCategorias(data);
        }
        if (rEbook.ok) {
          const data = await rEbook.json();
          if (Array.isArray(data)) setEbooks(data);
        }
      } catch (e) {
        console.error("Erro ao buscar autores/categorias:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [API_URL]);

  // Atualiza URL ao mudar categoria
  const setCategoria = (c) => {
    setCategoriaSel(c);
    // O bottom-sheet NÃO fecha ao escolher, como em /livros: o filtro é
    // aplicado na hora e o rodapé mostra a contagem nova, que é o retorno
    // imediato do toque. Quem terminou fecha no "Ver N autores".
    const p = new URLSearchParams(location.search);
    if (c) p.set("categoria", c); else p.delete("categoria");
    navigate(`${location.pathname}?${p.toString()}`, { replace: true });
  };

  // ====== Estatísticas por autor (derivadas dos ebooks) ======
  // O endpoint /autores só traz {id, nome, email}; então contamos publicações e
  // categorias percorrendo /ebook, onde cada item traz autores [{id, nome}] e categorias.
  const statsPorAutor = useMemo(() => {
    const map = new Map();
    ebooks.forEach((e) => {
      const cats = Array.isArray(e.categorias) ? e.categorias.map(nomeCategoria).filter(Boolean) : [];
      const autoresEbook = Array.isArray(e.autores) ? e.autores : [];
      autoresEbook.forEach((a) => {
        const id = typeof a === "object" ? a?.id : null;
        if (id == null) return;
        if (!map.has(id)) map.set(id, { count: 0, vendas: 0, categorias: new Set() });
        const s = map.get(id);
        s.count += 1;
        // VENDAS DO AUTOR = soma das vendas dos livros dele. O número por livro
        // vem do catálogo (`vendas`, exemplares com pagamento confirmado); aqui
        // ele só é somado. Livro com dois autores conta para os dois: a página
        // ordena PESSOAS, e os dois participaram daquela venda.
        s.vendas += Number(e.vendas) || 0;
        cats.forEach((c) => s.categorias.add(c));
      });
    });
    return map;
  }, [ebooks]);

  const contaLivros = (a) => statsPorAutor.get(a.id)?.count ?? 0;

  // ====== Aplicação dos filtros ======
  //
  // A CATEGORIA FICA DE FORA DESTE PRIMEIRO PASSO, de propósito: `semCategoria`
  // é o conjunto que passou por tudo MENOS pela categoria, e é dele que sai a
  // contagem de cada linha do painel — "quantos autores eu veria SE clicasse
  // aqui". Filtrado pela categoria atual, o número ao lado de cada nome já
  // viria errado. É o mesmo desenho de GridProduto.js, e pelo mesmo motivo:
  // duas contas escritas separadamente divergem no primeiro ajuste.
  const semCategoria = useMemo(() => {
    if (!buscaURL) return autores;
    const t = buscaURL.toLowerCase();
    return autores.filter((a) => (a.nome || "").toLowerCase().includes(t));
  }, [autores, buscaURL]);

  const contagemPorCategoria = useMemo(() => {
    const conta = new Map();
    semCategoria.forEach((a) => {
      const cats = statsPorAutor.get(a.id)?.categorias;
      if (!cats) return;
      cats.forEach((nome) => conta.set(nome, (conta.get(nome) || 0) + 1));
    });
    return conta;
  }, [semCategoria, statsPorAutor]);

  const filtrados = useMemo(() => {
    if (!categoriaSel) return semCategoria;
    // Exclui quem tem categorias conhecidas e nenhuma bate. Autor sem livro (ou
    // sem categoria cadastrada) continua visível: sumir com ele seria afirmar,
    // a partir de um dado que falta, que ele não é daquela área.
    return semCategoria.filter((a) => {
      const cats = statsPorAutor.get(a.id)?.categorias;
      return !cats || cats.size === 0 || cats.has(categoriaSel);
    });
  }, [semCategoria, categoriaSel, statsPorAutor]);

  // Ordenação
  const ordenados = useMemo(() => {
    const arr = [...filtrados];
    if (ordem === "az") arr.sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));
    if (ordem === "za") arr.sort((a, b) => (b.nome || "").localeCompare(a.nome || "", "pt-BR"));
    if (ordem === "publicacoes") {
      arr.sort((a, b) => (statsPorAutor.get(b.id)?.count ?? 0) - (statsPorAutor.get(a.id)?.count ?? 0));
    }
    // "Mais Popular" = quem mais VENDEU (pedido do dono, 11/09/2026). Antes
    // esta opção se chamava "Mais Relevante" e ordenava por número de
    // publicações — dois nomes para a mesma conta, e nenhum deles falando de
    // leitor. Empate cai no número de publicações e depois no nome, para a
    // lista não trocar de ordem a cada carregamento entre quem tem zero venda.
    if (ordem === "popular" || ordem === "relevante") {
      arr.sort((a, b) => {
        const va = statsPorAutor.get(a.id) || { vendas: 0, count: 0 };
        const vb = statsPorAutor.get(b.id) || { vendas: 0, count: 0 };
        return (vb.vendas - va.vendas)
          || (vb.count - va.count)
          || (a.nome || "").localeCompare(b.nome || "", "pt-BR");
      });
    }
    return arr;
  }, [filtrados, ordem, statsPorAutor]);

  // Paginação — ver AUTORES_PER_PAGE para a escolha do número
  useEffect(() => { setPagina(1); }, [categoriaSel, buscaURL, ordem]);
  const totalPaginas = Math.ceil(ordenados.length / AUTORES_PER_PAGE);
  const paginaAtual = Math.min(pagina, Math.max(1, totalPaginas));
  const itensPagina = ordenados.slice((paginaAtual - 1) * AUTORES_PER_PAGE, paginaAtual * AUTORES_PER_PAGE);

  const ordemLabel = ORDENS.find((o) => o.key === ordem)?.label || "Mais Popular";

  // Tudo o que os dois painéis (popup e bottom-sheet) precisam, num pacote
  // só: os dois leem O MESMO objeto, senão o estado nascido dentro de um deles
  // divergiria do outro ao cruzar 768px (girar um tablet).
  const propsFiltros = {
    categorias: categorias.map(nomeCategoria).filter(Boolean),
    categoriaSel,
    aoEscolherCategoria: setCategoria,
    contagemPorCategoria,
    totalSemCategoria: semCategoria.length,
    buscaCategoria,
    aoBuscarCategoria: setBuscaCategoria,
  };

  const limparTudo = () => {
    setBuscaCategoria("");
    setTextoBusca("");
    setCategoriaSel("");
    const q = new URLSearchParams(location.search);
    q.delete("categoria");
    q.delete("busca");
    navigate(`${location.pathname}${q.toString() ? "?" + q.toString() : ""}`, { replace: true });
  };
  const tituloCategoria = categoriaSel || "Todos os Autores";

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-12 text-white">
      <div className="max-w-[1280px] mx-auto">
        {/* Breadcrumb */}
        <nav className="text-xs text-white/60 mb-6 flex gap-2">
          {/* Mesmo sublinhado que cresce do centro do "Início" do cabeçalho.
              Inerte no toque: o traço nasce com largura zero. */}
          <Link to={main} className="link-sublinhado hover:!text-insigne-orange !text-white/60">Início</Link>
          <span>›</span>
          {categoriaSel && (
            <>
              <span>{categoriaSel}</span>
              <span>›</span>
            </>
          )}
          <span className="text-insigne-orange">Autores</span>
        </nav>

        {/* O "Autores" de 36px saiu a pedido do dono (11/09/2026): o
            cabeçalho da faixa já diz "Todos os Autores" (ou o nome da
            categoria) e o caminho logo acima também — três vezes a mesma
            palavra, uma delas ocupando meia dobra da tela. */}

        {/* --topo-fixo = onde está, NESTE instante, a borda de baixo do cabeçalho
            do site. Declarado uma vez aqui porque as duas colunas grudam nele; se
            cada uma calculasse o seu, bastaria uma divergir para as duas
            desalinharem no meio da rolagem.

              --nav-h    = altura do cabeçalho MAIS a folga que toda página usa de
                           padding no topo (224px a partir de 768px, para um
                           cabeçalho de ~184px — a conta está no App.css);
              --nav-topo = 0 com o cabeçalho à vista, -(--nav-h) quando ele sai de
                           cena ao rolar para baixo (useNavbarOculta). É o mesmo
                           mecanismo da barra do leitor (Leitor.css), e é o que faz
                           um bloco grudado acompanhar o cabeçalho em vez de deixar
                           um buraco onde ele estava;
              -40px      = a folga embutida em --nav-h. Sem descontá-la, a faixa
                           grudaria 40px abaixo do cabeçalho e a grade apareceria
                           rolando nessa fresta. Só o valor de 768px+ importa:
                           nada aqui gruda no celular;
              max(0px, …) = piso, para a conta nua não levar a faixa para fora
                           da tela com o título decepado.

            O RESPIRO DO TOPO NÃO MORA NO `top`, e sim em `md:pt-4` dentro das
            duas caixas, com `md:-mt-4` devolvendo os 16px ao fluxo. Com o
            respiro no `top`, aqueles 16px viravam fresta TRANSPARENTE acima da
            faixa, e a grade aparecia rolando por ela — fundo não pinta onde a
            caixa não está. Ver o comentário gêmeo em GridProduto.js.

            Era `top-[185px]` chumbado: acertava o cabeçalho à vista e deixava
            185px de tela vazia acima da faixa quando ele sumia. */}
        {/* A GRADE OCUPA A LARGURA INTEIRA desde 11/09/2026: as categorias
            viraram o popup do cabeçalho (ver BotaoCategorias). Este `div`
            continua aqui porque é ele quem define `--topo-fixo`, a altura em
            que a faixa de cabeçalho gruda. */}
        <div className="md:[--topo-fixo:max(0px,calc(var(--nav-h)_+_var(--nav-topo)_-_40px))]">
          {/* =================== CONTEÚDO =================== */}
          <main className="min-w-0">
            {/* Cabeçalho: categoria + contagem + busca + Categorias + ordem.
                ACOMPANHA O SCROLL no desktop: quem chegava na altura do autor
                40 ficava sem contagem e sem ordenação, tendo de rolar tudo de
                volta para lembrar qual categoria estava filtrada. Com a coluna
                de categorias fora, ela é o único lugar onde esses controles
                existem. É o mesmo pedido e o mesmo padrão de GridProduto.js:
                fundo opaco e camada própria (`md:z-30`, dentro de
                GRUDA_NO_TOPO) para a grade não pintar por cima nem passar por
                baixo do texto; `md:-mt-4 md:pt-4` faz o fundo chegar até a
                borda da janela sem levar o texto junto.

                O RESPIRO DE BAIXO É `mb-6`, MARGEM, e continua sendo. Ele
                chegou a virar `md:pb-6` (padding, pintado com o fundo) num
                diagnóstico errado da sombra que vazava à direita — aquela
                vazava PELA LATERAL, ver `md:-mx-3` abaixo. O padding opaco
                cobria a FITA do primeiro cartão da grade, que se projeta ~10px
                acima do papel e nasce dentro daquele vão; como a faixa é
                `z-30`, ela ganhava, e a fita aparecia cortada com a página
                parada.

                A borda mora num `<div>` interno com o respiro de sempre
                (`md:pb-3`), e é ele — não o `<div>` externo — que define onde a
                linha aparece. Nada disso vale no celular: cabeçalho preso lá
                come a altura útil da tela.

                `md:-mx-3 md:px-3`: o fundo é 12px mais largo que a coluna
                de cada lado, com os mesmos 12px devolvidos ao conteúdo. A
                sombra sólida do cartão de autor se projeta 10px para fora dele
                no hover (`.card-autor` no App.css), e sem essa folga ela
                aparecia como um risco vertical à direita da faixa quando a
                fileira passava por baixo. Mesmo conserto de GridProduto.js. */}
            <div ref={faixaSentinelaRef} aria-hidden="true" className="h-0" />
            <div
              ref={faixaRef}
              className={"mb-6 md:-mt-4 md:pt-4 md:-mx-3 md:px-3 md:bg-insigne-bg " +
                         GRUDA_NO_TOPO + " " + (esconderFaixa ? CLASSES_ESCONDIDA : "")}
            >
              <div className="md:pb-3 md:border-b md:border-line">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="titulo-display text-[2rem]">{tituloCategoria}</h2>
                    <p className="text-white/60 text-sm mt-1">
                      {ordenados.length > 0
                        ? <>Mostrando {(paginaAtual - 1) * AUTORES_PER_PAGE + 1}-{Math.min(paginaAtual * AUTORES_PER_PAGE, ordenados.length)} de {ordenados.length} autor{ordenados.length === 1 ? "" : "es"}:</>
                        : <>Nenhum autor encontrado</>}
                    </p>
                  </div>

                  {/* ===== A BUSCA DESTA PÁGINA (pedido do dono, 11/09/2026) =====
                      Procurar autor exigia subir até a barra do cabeçalho do
                      site, que some ao rolar. Aqui ela fica na faixa que
                      acompanha a rolagem, onde a pessoa já está olhando.
                      A ORDEM NA FILEIRA: no celular a busca desce para a linha
                      de baixo (`order-[99]`); do tablet para cima vale a ordem do
                      código — título, busca e, À DIREITA dela, os filtros (pedido
                      do dono, 11/09/2026). Era `order-last md:order-none`, e o
                      Bootstrap anulava a segunda metade: o `.order-last` dele
                      tem `!important`, então a busca ficava por último também no
                      desktop e os filtros apareciam à esquerda dela. Valor
                      arbitrário não tem homônimo no Bootstrap. */}
                  <div className="relative flex-1 min-w-[min(100%,220px)] order-[99] md:order-[0]">
                    <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
                    <input
                      type="search"
                      value={textoBusca}
                      onChange={(e) => {
                        setTextoBusca(e.target.value);
                        const q = new URLSearchParams(location.search);
                        if (e.target.value) q.set("busca", e.target.value);
                        else q.delete("busca");
                        navigate(`${location.pathname}${q.toString() ? "?" + q.toString() : ""}`, { replace: true });
                      }}
                      placeholder="Buscar autor pelo nome..."
                      aria-label="Buscar autor"
                      className="foco-laranja w-full rounded-md border border-line bg-white/5 py-2 pl-10 pr-3
                                 text-sm text-white placeholder:text-white/40 outline-none
                                 transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
                    />
                  </div>

                  {/* Categorias + ordem, lado a lado: os dois controlam o
                      que se vê, e separá-los obrigaria a procurar em dois
                      cantos da tela. O botão de categorias abre o popup no
                      desktop e o bottom-sheet no celular (ver
                      BotaoCategorias). */}
                  <div className="flex items-center gap-2">
                  <BotaoCategorias
                    filtros={propsFiltros}
                    aoAbrirNoCelular={() => setPainelAberto(true)}
                  />

                  {/* Dropdown de ordenação */}
                  <div className="relative">
                    <button
                      onClick={() => setDropdownOrdem((v) => !v)}
                      className="flex items-center gap-3 px-4 py-2 rounded-md border border-line
                                 bg-white/5 hover:bg-white/10 text-sm text-white/85 min-w-[180px] justify-between"
                    >
                      {ordemLabel}
                      <i className="bi bi-chevron-down text-xs" />
                    </button>
                    {dropdownOrdem && (
                      <ul className="absolute right-0 mt-2 w-[200px] rounded-md bg-insigne-navy-deep
                                     border border-line shadow-lg z-30 overflow-hidden">
                        {ORDENS.map((o) => (
                          <li key={o.key}>
                            <button
                              onClick={() => { setOrdem(o.key); setDropdownOrdem(false); }}
                              className={
                                "w-full text-left px-4 py-2 text-sm hover:bg-white/5 " +
                                (ordem === o.key ? "text-insigne-orange" : "text-white/85")
                              }
                            >
                              {o.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading / Empty / Grade */}
            {loading ? (
              <p className="text-white/60">Carregando...</p>
            ) : ordenados.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-20">
                <CaixaVazia />
                <h3 className="titulo-display text-[2rem] mt-6">
                  Nada encontrado para "{buscaURL || categoriaSel || "esses filtros"}"
                </h3>
                <p className="text-white/50 mt-1">Refaça sua busca e tente novamente</p>
              </div>
            ) : (
              <>
              {/* Mesmo cartão "folha de caderno" e mesma grade da home: era o
                  único caso do site em que um vocabulário inteiro já escrito
                  (as ~200 linhas de .card-autor no App.css) era ignorado pela
                  tela que mais deveria usá-lo — os MESMOS autores apareciam
                  como cartão na home e como linha de lista aqui.

                  A calha VERTICAL é maior que a horizontal de propósito: o
                  cartão cresce ~58px para baixo quando abre (46px de papel
                  mais 12px de sombra sólida), e com os 24px de antes ele
                  pousava em cima do cartão da fileira seguinte. A conta tem
                  que incluir a FITA do cartão de baixo, que se projeta ~10px
                  acima do papel dele: com 64px de calha sobravam 5px até o
                  cartão, mas -5px até a fita. O espaço fica
                  reservado sempre — folga que só existe no hover seria o
                  mesmo empurrão que a expansão foi feita para evitar.
                  `gap-y-16` e `gap-x-*`: nomes com eixo e número >= 6 estão
                  fora do alcance da escala 0–5 que o Bootstrap redefine com
                  !important (o `gap-4` daqui valia 1,5rem, não 1rem). */}
              <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-20 orfa-ao-centro">
                {itensPagina.map((autor, i) => {
                  const foto = resolveFoto(autor, API_URL);
                  const qtdLivros = contaLivros(autor);
                  return (
                    <RevealStagger.Item key={autor.id ?? autor.nome ?? i} className="h-full">
                      <Link
                        to={main + "autores/" + autor.id}
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="card-autor h-full"
                      >
                        {/* Rabiscos decorativos */}
                        <svg className="rabisco estrela" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 2L15 9L22 10L17 15L18.5 22L12 18.5L5.5 22L7 15L2 10L9 9L12 2Z" />
                        </svg>
                        <svg className="rabisco faisca" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 0C12 6.6 17.4 12 24 12C17.4 12 12 17.4 12 24C12 17.4 6.6 12 0 12C6.6 12 12 6.6 12 0Z" />
                        </svg>
                        <svg className="rabisco espiral" viewBox="0 0 100 100" aria-hidden="true">
                          <path d="M50 10C27.9 10 10 27.9 10 50C10 72.1 27.9 90 50 90C72.1 90 90 72.1 90 50C90 32.3 75.7 18 58 18C44.3 18 33 29.3 33 43C33 53.5 41.5 62 52 62C59.7 62 66 55.7 66 48" />
                        </svg>

                        {/* Foto real do autor; sem foto, a marca da Insigne */}
                        <div className="card-autor-foto">
                          {foto ? (
                            <img src={foto} alt={autor.nome} loading="lazy" className="w-full h-full object-cover" />
                          ) : (
                            <MarcaPadrao />
                          )}
                        </div>

                        <p className="card-autor-nome" title={autor.nome || "Autor sem nome"}><span>{autor.nome || "Autor sem nome"}</span></p>

                        {/* O selo carrega a contagem de publicações — o dado que a
                            pílula da lista antiga mostrava e que a home não tem.
                            "livros" e não "publicações": o selo é caixa alta com
                            0,1em de entreletras e a duas colunas sobram ~120px de
                            cartão — "12 PUBLICAÇÕES" quebraria em duas linhas e aí
                            os cartões vizinhos voltariam a ficar em alturas
                            diferentes. Sem livro contado, o selo diz "Autor",
                            como na home. */}
                        <span className="card-autor-cargo">
                          {qtdLivros > 0
                            ? `${qtdLivros} ${qtdLivros === 1 ? "livro" : "livros"}`
                            : "Autor"}
                        </span>

                        <span className="card-autor-acao">
                          <span className="card-autor-btn">Ver mais</span>
                        </span>
                      </Link>
                    </RevealStagger.Item>
                  );
                })}
              </RevealStagger>
              {/* Mesma folga da home: o cartão da última fileira cresce ~58px
                  para baixo quando abre, e sem isso ele pousaria em cima da
                  paginação. */}
              <div className="pb-14" />
              <Pagination pagina={paginaAtual} totalPaginas={totalPaginas} onChange={setPagina} />
              </>
            )}
          </main>
        </div>
      </div>

      {/* Bottom-sheet dos filtros do celular. Fica FORA do `flex` do layout de
          propósito: dentro daquele container ele herdaria o contexto de
          empilhamento da coluna e o `z-[9998]` passaria a valer só ali dentro,
          ficando por baixo do cabeçalho. Recebe o MESMO `propsFiltros` do
          popup. `total` é a contagem já filtrada: como o filtro é aplicado na
          hora, o número no rodapé é o retorno imediato de cada toque. */}
      <DrawerFiltros
        aberto={painelAberto}
        onFechar={() => setPainelAberto(false)}
        total={ordenados.length}
        onLimparTudo={limparTudo}
        filtros={propsFiltros}
      />
    </div>
  );
}

export default AutoresGrid;
